import clsx from 'clsx';
import { memo, useMemo } from 'react';
import { ImageTagViewModel } from '~/lib/data-contract';
import { useTranslation } from '~/shared/utils';
import { weakKey } from '~/shared/utils/jsx';
import { VisuallyHidden } from '../VisuallyHidden';
import { Typography } from '../typography/typography.component';
import styles from './product-tags.module.css';

const CAMPAIGN_TAG_VARIANTS: ImageTagViewModel['variant'][] = [
    'Campaign',
    'Price',
    'Deal',
    'LowestPrice30Days',
    'PersistentDiscount',
];

const VARIANT_CLASSES: Record<NonNullable<ImageTagViewModel['variant']>, string> = {
    Campaign: styles.campaign,
    Price: styles.promotion,
    Deal: styles.promotion,
    LowestPrice30Days: styles.promotion,
    PersistentDiscount: styles.promotion,
};

export type ProductTagsLayout = 'list' | 'spaced-list';

type Props = {
    productTags?: ImageTagViewModel[];
    selectFrom?: 'bottom' | 'top' | 'all';
    layout?: ProductTagsLayout;
    isCampaign?: boolean;
    className?: string;
};

const ProductTagsComponent = memo(
    ({
        productTags = [],
        selectFrom = 'all',
        layout = 'list',
        isCampaign = false,
        className,
    }: Props) => {
        const { translate } = useTranslation();
        const isAttribute = selectFrom === 'bottom';

        const filteredTags = useMemo(
            () =>
                productTags.filter((tag) => {
                    const matchesCampaign =
                        CAMPAIGN_TAG_VARIANTS.includes(tag.variant) === isCampaign;

                    if (selectFrom === 'all') {
                        return matchesCampaign;
                    }

                    return matchesCampaign && !!tag.placeholder?.toLowerCase().includes(selectFrom);
                }),
            [productTags, selectFrom, isCampaign]
        );

        if (!filteredTags.length) {
            return null;
        }

        const ListTag = isAttribute ? 'dl' : 'ul';
        const ItemTag = isAttribute ? 'dd' : 'li';

        return (
            <ListTag
                className={clsx(
                    layout === 'spaced-list' ? styles.spacedList : styles.list,
                    className
                )}
            >
                {isAttribute ? (
                    <VisuallyHidden asChild>
                        <dt>{translate('accessibility.productAttributes')}</dt>
                    </VisuallyHidden>
                ) : null}
                {filteredTags.map((tag) => {
                    const variantClass = isAttribute
                        ? styles.attribute
                        : tag.variant && VARIANT_CLASSES[tag.variant];

                    return (
                        <ItemTag key={weakKey(tag)}>
                            <Typography
                                as="span"
                                // variant={isAttribute ? 'bodyXs' : 'bodySm'}
                                variant={'bodyXs'}
                                className={clsx(styles.tag, variantClass)}
                                // CMS supplies arbitrary hex colours for tags without a known variant
                                style={
                                    variantClass
                                        ? undefined
                                        : {
                                              backgroundColor: tag.backgroundColor,
                                              color: tag.fontColor,
                                          }
                                }
                            >
                                {tag.text}
                            </Typography>
                        </ItemTag>
                    );
                })}
            </ListTag>
        );
    }
);

ProductTagsComponent.displayName = 'ProductTags';

export { ProductTagsComponent as ProductTags };
